'use client'

import * as React from 'react'
import { CodeBlockIcon } from '@/tiptap/tiptap-icons/code-block-icon'
// --- UI Primitives ---
import { Button } from '@/tiptap/tiptap-ui-primitive/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/tiptap/tiptap-ui-primitive/popover'
// --- Tiptap UI ---
import { headingIcons } from '@/tiptap/tiptap-ui/heading-button/heading-button'
import { Editor } from '@tiptap/react'
import { Check, ChevronDown } from 'lucide-react'
// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor'

// --- Icons ---

export interface NodeSelectorProps {
  editor?: Editor | null
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

interface NodeTypeOption {
  name: string
  description: string
  icon: React.ComponentType<any>
  command: (editor: Editor) => void
  isActive: (editor: Editor) => boolean
}

const nodeTypeOptions: NodeTypeOption[] = [
  {
    name: 'Text',
    description: 'Just start typing with plain text.',
    icon: CodeBlockIcon,
    command: (editor) => editor.chain().focus().setParagraph().run(),
    isActive: (editor) => editor.isActive('paragraph'),
  },
  {
    name: 'Heading 2',
    description: 'Medium section heading.',
    icon: headingIcons[2],
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
  },
  {
    name: 'Heading 3',
    description: 'Small section heading.',
    icon: headingIcons[3],
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
  },
  {
    name: 'Bullet List',
    description: 'Create a simple bullet list.',
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
      >
        <line x1='8' y1='6' x2='21' y2='6' />
        <line x1='8' y1='12' x2='21' y2='12' />
        <line x1='8' y1='18' x2='21' y2='18' />
        <line x1='3' y1='6' x2='3.01' y2='6' />
        <line x1='3' y1='12' x2='3.01' y2='12' />
        <line x1='3' y1='18' x2='3.01' y2='18' />
      </svg>
    ),
    command: (editor) => editor.chain().focus().toggleBulletList().run(),
    isActive: (editor) => editor.isActive('bulletList'),
  },
  {
    name: 'Numbered List',
    description: 'Create a list with numbering.',
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
      >
        <line x1='10' y1='6' x2='21' y2='6' />
        <line x1='10' y1='12' x2='21' y2='12' />
        <line x1='10' y1='18' x2='21' y2='18' />
        <path d='M4 6h1v4' />
        <path d='M4 10h2' />
        <path d='M6 18H4c0-1 2-2 2-3s-1-1.5-2-1' />
      </svg>
    ),
    command: (editor) => editor.chain().focus().toggleOrderedList().run(),
    isActive: (editor) => editor.isActive('orderedList'),
  },
  {
    name: 'To-do List',
    description: 'Track tasks with a to-do list.',
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
      >
        <path d='M9 11l3 3L22 4' />
        <path d='M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11' />
      </svg>
    ),
    command: (editor) => editor.chain().focus().toggleTaskList().run(),
    isActive: (editor) => editor.isActive('taskList'),
  },
  {
    name: 'Quote',
    description: 'Capture a quote.',
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
      >
        <path d='M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z' />
        <path d='M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z' />
      </svg>
    ),
    command: (editor) => editor.chain().focus().toggleBlockquote().run(),
    isActive: (editor) => editor.isActive('blockquote'),
  },
  {
    name: 'Code',
    description: 'Capture a code snippet.',
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
      >
        <polyline points='16,18 22,12 16,6' />
        <polyline points='8,6 2,12 8,18' />
      </svg>
    ),
    command: (editor) => editor.chain().focus().toggleCodeBlock().run(),
    isActive: (editor) => editor.isActive('codeBlock'),
  },
]

export function NodeSelector({
  editor: providedEditor,
  open,
  onOpenChange,
}: NodeSelectorProps) {
  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = React.useState(open || false)

  React.useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open)
    }
  }, [open])

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setIsOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [onOpenChange]
  )

  const getActiveNodeType = React.useCallback(() => {
    if (!editor) return nodeTypeOptions[0]

    const activeOption = nodeTypeOptions.find((option) =>
      option.isActive(editor)
    )
    return activeOption || nodeTypeOptions[0]
  }, [editor])

  const activeNodeType = getActiveNodeType()
  const ActiveIcon = activeNodeType.icon

  if (!editor || !editor.isEditable) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          data-style='ghost'
          size='sm'
          className='gap-2 !rounded-md border-none'
        >
          <ActiveIcon className='h-4 w-4' />
          <span className='text-sm whitespace-nowrap'>
            {activeNodeType.name}
          </span>
          <ChevronDown className='h-4 w-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-60 p-1'>
        <div className='space-y-1'>
          {nodeTypeOptions.map((option) => {
            const Icon = option.icon
            const isActive = option.isActive(editor)

            return (
              <button
                key={option.name}
                onClick={() => {
                  option.command(editor)
                  handleOpenChange(false)
                }}
                className={`text-foreground hover:bg-accent flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm ${
                  isActive ? 'bg-accent' : ''
                }`}
              >
                <div className='flex items-center gap-2'>
                  <Icon className='h-4 w-4' />
                  <div className='flex flex-col'>
                    <span className='font-medium'>{option.name}</span>
                    <span className='text-muted-foreground text-xs'>
                      {option.description}
                    </span>
                  </div>
                </div>
                {isActive && <Check className='h-4 w-4' />}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
